'use client';

import { datadogRum } from '@datadog/browser-rum';
import { type Ref, type SyntheticEvent, useCallback, useState } from 'react';

interface VideoPlayerProps {
  src: string;
  // PH-4902: forces a download (Content-Disposition: attachment); falls back to opening src.
  downloadUrl?: string | null;
  mediaId?: number | string | null;
  // Which viewer rendered the video, for the RUM error.
  source: string;
  ref?: Ref<HTMLVideoElement>;
}

type FailureReason = 'error' | 'no_video_track';

// MEDIA_ERR_DECODE, MEDIA_ERR_SRC_NOT_SUPPORTED.
const UNPLAYABLE_ERROR_CODES = new Set([3, 4]);

// PH-4902: some uploads (camera-DVR MPEG-4 Part 2, HEVC on Windows Chrome) are videos the browser
// can't decode. Give the viewer a download path instead of a blank player, and report it to RUM.
// Callers key this by src so a new video starts with a fresh state.
const VideoPlayer = ({ src, downloadUrl, mediaId, source, ref }: VideoPlayerProps) => {
  const [failed, setFailed] = useState(false);

  const fail = useCallback((reason: FailureReason, video: HTMLVideoElement) => {
    setFailed(true);
    // Never send the URL — it is signed.
    datadogRum.addError(new Error('video_playback_failed'), {
      source,
      reason,
      media_id: mediaId ?? null,
      media_error_code: video.error?.code ?? null,
    });
  }, [mediaId, source]);

  // Only a file the browser can't decode or doesn't support (3, 4) means "can't play here".
  // Aborted (1) and network (2) errors are transient: keep the player so the viewer can retry.
  const handleError = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const code = event.currentTarget.error?.code;
    if (code !== undefined && !UNPLAYABLE_ERROR_CODES.has(code)) return;
    fail('error', event.currentTarget);
  }, [fail]);

  // An unsupported video track next to a playable audio track plays sound over a blank frame and
  // never fires `error`.
  const handleLoadedMetadata = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    if (video.videoWidth === 0 && video.videoHeight === 0) fail('no_video_track', video);
  }, [fail]);

  if (failed) {
    return (
      <div className="video-player-fallback" role="alert">
        {downloadUrl ? (
          <>
            <p className="video-player-fallback__message">
              This video can&apos;t be played in your browser. Download it to watch in a desktop player
              such as VLC or Windows Media Player.
            </p>
            <a className="link-btn video-player-fallback__download" href={downloadUrl}>Download video</a>
          </>
        ) : (
          <p className="video-player-fallback__message">
            This video isn&apos;t supported by your browser at the moment.
          </p>
        )}
      </div>
    );
  }

  return (
    <video ref={ref} src={src} controls onError={handleError} onLoadedMetadata={handleLoadedMetadata}>
      <track kind="captions" />
    </video>
  );
};

export default VideoPlayer;
